import { useEffect, useMemo, useState } from 'react';
import { baseUrl } from '../constants';
import { IPlan, ISubscription, ISubscriptionPlan, ItemTypeEnum } from '../model';
import { IAccess } from '../context/AppSettings.context';
import { TotalsType } from '../components/Paywall/Paywall';

export type ISubscriptionWithPlan = IPlan & {
  plan?: IPlan;
  current: ISubscriptionPlan;
  subscription: ISubscription;
}

const getSport = (sport: string) => {
  if (sport === 'nfl') return 'nfl';
  if (sport === 'mlb') return 'mlb';
  return 'other';
}

export const usePaywallPlans = (
  subscriptions: ISubscription[],
  accesses: IAccess[],
  yearly: boolean
) => {
  const [plans, setPlans] = useState<IPlan[]>([]);

  useEffect(() => {
    // @ts-ignore
    fetch(`${baseUrl}/plans/`)
      .then(res => res.json())
      .then((response) => {
        setPlans(response.map((plan: IPlan) => {
          if(plan.display.sport_category === 'Stats'){
            return {
              ...plan,
              prices: {
                yearly: plan.prices.yearly
              }
            };
          }
          return {
            ...plan,
            type: ItemTypeEnum.plan
          }
        }));
      });
  }, []);

  const {
    subscribedPlans,
    maxTier,
    subscribedSport
  } = useMemo(() => {
    let maxTier = 0;
    let subscribedSport: string | undefined;

    const subscribedPlans = subscriptions.map(subscription => subscription.plans.map(plan => {
      const foundPlan = plans?.find(p => p.plan_id === plan.plan_id);
      if(foundPlan?.plan_tier ?? 0 > maxTier) {
        maxTier = foundPlan?.plan_tier || 0;
        subscribedSport = foundPlan?.display?.sport_category;
      }
      return {
        plan: foundPlan,
        current: plan,
        subscription
      }
    })).flat(1) as ISubscriptionWithPlan[];

    return {
      subscribedPlans,
      maxTier,
      subscribedSport
    }
  }, [subscriptions, plans]);

  const {
    recommendedPlan,
    bestPlan,
    currentSubscriptions,
    currentPlan,
    total
  } = useMemo(() => {
    const currentSubscriptions: Record<string, number> = {};

    const subscribedPlanIds = subscribedPlans.map(plan => plan.plan_id);
    plans.forEach((plan) => {
      if (subscribedPlanIds.includes(plan.plan_id)) {
        if (currentSubscriptions[plan.display.sport_category] && currentSubscriptions[plan.display.sport_category] > plan.plan_tier) {
          return
        }
        currentSubscriptions[plan.display.sport_category] = plan.plan_tier
      }
    });

    const total: TotalsType = {
      'nfl': 0,
      'mlb': 0,
      'other': 0
    };

    subscribedPlans.forEach((sub) => {
      let price = Number(sub?.current.price);
      if(sub?.current.interval === 'year') price = price / 12;

      if(sub.plan?.display.sport_category === 'nfl') total.nfl += price;
      if(sub.plan?.display.sport_category === 'mlb') total.mlb += price;
      total.other += price;
    })

    const sortedPlans = plans
      .filter(plan => yearly ? plan.prices?.yearly : plan.prices?.monthly) // Filter plans that have prices for the selected interval
      .filter(plan => maxTier <= plan.plan_tier) // Filter plans that are at or above the max tier of subscribed plans
      .filter(plan => subscribedSport ? plan.display.sport_category === subscribedSport || plan.display.sport_category === 'all' : true) // Filter by subscribed sport or 'all'
      .sort((plan1, plan2) => (
          plan1 ? plan1.prices?.yearly?.unit_amount || 0 - total[getSport(plan1.display.sport_category)] * 12 : 0)
        - (plan2 ? plan2.prices?.yearly?.unit_amount || 0 - total[getSport(plan2.display.sport_category)] * 12 : 0)
      )

    const hasStats = !!currentSubscriptions['Stats'];

    let recommendedPlan;
    let currentPlan;
    const possiblePlans = accesses
      .map(access => {
        return sortedPlans.find(plan => plan.permissions_tiers[access.realm]?.[access.sport] >= access.tier);
      })
      .filter(Boolean)
      .sort((plan1, plan2) => (
          plan1 ? plan1.prices?.yearly?.unit_amount || 0 - total[getSport(plan1.display.sport_category)] * 12 : 0)
        - (plan2 ? plan2.prices?.yearly?.unit_amount || 0 - total[getSport(plan2.display.sport_category)] * 12 : 0)
      );

    if(subscribedPlans.length <= 1) {
      if(hasStats) {
        recommendedPlan = possiblePlans.find(plan => plan?.display.sport_category === 'Stats') ?? possiblePlans[0];
      } else {
        if(subscribedPlans.length === 0){
          recommendedPlan = possiblePlans[0];
        } else {
          recommendedPlan = possiblePlans.find(plan => plan?.display.sport_category !== 'Stats') ?? possiblePlans[0];

        }
      }
      currentPlan = subscribedPlans[0];
    } else {
      const firstPlan = possiblePlans[0];
      if(hasStats){
        if(firstPlan?.display.sport_category === 'Stats'){
          currentPlan = subscribedPlans.find(sub => sub.plan?.display?.sport_category === 'Stats');
        } else {
          currentPlan = subscribedPlans.find(sub => sub.plan?.display?.sport_category !== 'Stats');
        }
      } else {
        currentPlan = subscribedPlans.find(sub => sub.plan?.display?.sport_category !== 'Stats');
      }
      recommendedPlan = firstPlan;
    }

    const bestPlan = plans.find(plan => plan.plan_tier === 4 && plan.display.sport_category === 'all');

    return {
      currentPlan,
      recommendedPlan,
      bestPlan,
      currentSubscriptions,
      total
    }
  }, [plans, subscribedPlans, yearly])

  return {
    recommendedPlan,
    bestPlan,
    subscribedPlans,
    currentSubscriptions,
    currentPlan,
    total,
    allPlans: plans
  }
};
